import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Loader2, Check, X, AlertCircle, RotateCw, ChevronDown, ChevronUp } from 'lucide-react';
import { type UploadItem, queueStats, formatBytes, orderForDisplay } from '@/lib/upload';
import { useUploadEta } from '@/hooks/useUploadManager';

/** A failure in the reader's language when the transport tagged it; the server's words otherwise. */
export function uploadErrorText(t: TFunction, u: Pick<UploadItem, 'error' | 'code' | 'name'>): string {
  switch (u.code) {
    case 'EMPTY_FILE':
      return t('upload.errors.emptyFile', 'This file is empty or unreadable. Folders drop as 0-byte entries, so drop the files inside instead.');
    case 'FILE_GONE':
      return t('upload.errors.fileGone', 'File no longer available. Add it again to retry.');
    case 'UPLOADS_SUSPENDED':
      return t('library.uploadsSuspended', 'Uploads are paused on this account while it is reviewed. Contact support@soltik.app.');
    default:
      return u.error || t('upload.errors.generic', 'Upload failed');
  }
}

/** "about 3 min left" — coarse on purpose; transfer rates wobble. */
export function formatEta(t: TFunction, sec: number): string {
  if (sec < 60) return t('upload.etaSeconds', 'about {n} s left', { n: Math.max(5, Math.round(sec / 5) * 5) });
  if (sec < 3600) return t('upload.etaMinutes', 'about {n} min left', { n: Math.round(sec / 60) });
  const h = Math.floor(sec / 3600);
  return t('upload.etaHours', 'about {h} h {m} min left', { h, m: Math.round((sec % 3600) / 60) });
}

/**
 * Shared upload progress UI.
 *
 * One row per file, so a second drop mid-flight ADDS a line rather than
 * overwriting the name of the one already going. Percentages are per-file
 * bytes; the overall figure is bytes too, not files-completed — with
 * files-completed a single upload could only ever read 0% or 100%.
 *
 * Rows are ORDERED, not just capped. The strip used to render the first N
 * entries in queue order and print "+87 more" — so in a 93-file batch the
 * failures were almost never among the visible rows, and the user was told
 * five files had failed without being told WHICH, or given any way to reach
 * them. Failures now sort first and the full list is one click away, because
 * a count the user cannot act on is not feedback.
 *
 * Surfaces that need a different presentation can read the same queue and
 * render their own; only the transport and bookkeeping are meant to be shared.
 */

export default function UploadStrip({
  items,
  onCancel,
  onCancelAll,
  onRetryFailed,
  onRetryOne,
  onDismissErrors,
  maxRows = 6,
  variant = 'band',
}: {
  items: UploadItem[];
  onCancel?: (id: string) => void;
  onCancelAll?: () => void;
  onRetryFailed?: () => void;
  onRetryOne?: (id: string) => void;
  onDismissErrors?: () => void;
  maxRows?: number;
  /** 'band' sits under a page header; 'panel' fills the floating upload panel. */
  variant?: 'band' | 'panel';
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const s = queueStats(items);
  const eta = useUploadEta(s.loadedBytes, s.totalBytes, !s.settled && items.length > 0);
  if (items.length === 0) return null;

  const ordered = orderForDisplay(items);
  const visible = expanded ? ordered : ordered.slice(0, maxRows);
  const hidden = ordered.length - visible.length;

  return (
    <div className={variant === 'panel' ? 'px-3 py-2.5' : 'border-b border-amber-500/30 bg-amber-500/10 px-4 py-2.5'}>
      <div className="flex items-center gap-3 mb-1.5">
        {/* The panel's own header already carries the icon and the count. */}
        {variant === 'band' && (s.settled
          ? (s.failed > 0
            ? <AlertCircle size={14} className="text-red-300 shrink-0" />
            : <Check size={14} className="text-emerald-300 shrink-0" />)
          : <Loader2 size={14} className="text-amber-300 animate-spin shrink-0" />)}

        <div className={`${variant === 'panel' ? 'text-[11px] [&>span:first-child]:ms-0' : 'text-xs'} text-amber-100 flex-1 min-w-0`}>
          {variant === 'band' && (s.settled
            ? t('upload.finished', 'Uploaded {done} of {total}', { done: s.done, total: s.total })
            : t('upload.inProgress', 'Uploading {done}/{total}', { done: s.done, total: s.total }))}
          {s.failed > 0 && (
            <span className="text-red-300 ms-2 font-medium">
              {t('upload.failedCount', '{count} failed', { count: s.failed })}
            </span>
          )}
          {s.totalBytes > 0 && (
            <span className="text-amber-200/50 ms-2 tabular-nums">
              {formatBytes(s.loadedBytes)} / {formatBytes(s.totalBytes)} · {s.pct}%
            </span>
          )}
          {eta !== null && (
            <span className="text-amber-200/50 ms-2 tabular-nums">· {formatEta(t, eta)}</span>
          )}
        </div>

        {/* Cancel-all while anything is still in flight. */}
        {!s.settled && onCancelAll && (
          <button
            type="button"
            onClick={onCancelAll}
            className="text-[11px] font-medium text-red-300/80 hover:text-red-200 shrink-0"
          >
            {t('upload.cancelAll', 'Cancel all')}
          </button>
        )}
        {/* Retry every failure at once. Available while the rest of a long
            batch is still running — with 90 files, waiting for the whole
            queue to settle before you may retry is a long time to sit on a
            known failure. */}
        {s.failed > 0 && onRetryFailed && (
          <button
            type="button"
            onClick={onRetryFailed}
            className="text-[11px] font-medium text-amber-100 hover:text-white shrink-0"
          >
            {t('upload.retryFailed', 'Retry {count} failed', { count: s.failed })}
          </button>
        )}
        {s.failed > 0 && onDismissErrors && (
          <button
            type="button"
            onClick={onDismissErrors}
            className="text-[11px] text-amber-200/70 hover:text-amber-100 shrink-0"
          >
            {t('upload.dismiss', 'Dismiss')}
          </button>
        )}
      </div>

      {/* Scrolls once expanded so a 90-file batch is fully reachable without
          pushing the library itself off the screen. */}
      <div className={expanded ? 'space-y-1 max-h-64 overflow-y-auto pe-1' : 'space-y-1'}>
        {visible.map((u) => {
          const pct = u.status === 'done' ? 100 : u.size > 0 ? Math.round((u.loaded / u.size) * 100) : 0;
          const errText = u.status === 'error' ? uploadErrorText(t, u) : '';
          return (
            <div key={u.id} className="flex items-center gap-2">
              <div className="text-[11px] text-amber-100/80 truncate flex-1 min-w-0" title={errText ? `${u.name} — ${errText}` : u.name}>
                {u.name}
                {errText && (
                  <span className="text-red-300/70 ms-1.5">— {errText}</span>
                )}
              </div>

              <div className={`${variant === 'panel' ? 'w-20' : 'w-28'} h-1 bg-amber-500/15 rounded-full overflow-hidden shrink-0`}>
                <div
                  className={`h-full transition-all duration-150 ${
                    u.status === 'error' ? 'bg-red-400' : u.status === 'done' ? 'bg-emerald-400' : 'bg-amber-400'
                  }`}
                  style={{ width: `${u.status === 'error' ? 100 : pct}%` }}
                />
              </div>

              <div className="text-[10px] tabular-nums text-amber-200/60 w-9 text-end shrink-0">
                {u.status === 'error' ? t('upload.failedShort', 'fail') : `${pct}%`}
              </div>

              {/* Retry just this file. */}
              {onRetryOne && u.status === 'error' && (
                <button
                  type="button"
                  onClick={() => onRetryOne(u.id)}
                  className="text-amber-200/60 hover:text-amber-100 shrink-0"
                  aria-label={t('upload.retryOne', 'Retry this upload')}
                  title={t('upload.retryOne', 'Retry this upload')}
                >
                  <RotateCw size={12} />
                </button>
              )}

              {onCancel && (u.status === 'pending' || u.status === 'uploading') && (
                <button
                  type="button"
                  onClick={() => onCancel(u.id)}
                  className="text-amber-200/50 hover:text-amber-100 shrink-0"
                  aria-label={t('upload.cancel', 'Cancel upload')}
                  title={t('upload.cancel', 'Cancel upload')}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* The trim is a control, never a dead label. */}
      {(hidden > 0 || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 flex items-center gap-1 text-[10px] text-amber-200/70 hover:text-amber-100"
        >
          {expanded
            ? <><ChevronUp size={11} /> {t('upload.showLess', 'Show less')}</>
            : <><ChevronDown size={11} /> {t('upload.showAll', 'Show all {count} files', { count: ordered.length })}</>}
        </button>
      )}
    </div>
  );
}
